import React, { useEffect, useState } from 'react';
import { Form, FormInstance, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { getProductCurrentStock, type Product } from '../../api/productApi';
import { getProductStock, getStockAfterLine, sumStockByUnit } from '../../utils/stockHelpers';

type ItemStockHintProps = {
    form: FormInstance;
    /** Index of the row inside the `items` Form.List. */
    name: number;
    products: Product[];
    /** A sale takes stock out; a return or a purchase puts it back in. */
    type: 'sale' | 'return' | 'purchase';
};

type LiveStock = {
    productId: string;
    byUnit: Record<string, number>;
};

/**
 * The stock of one invoice line, recalculated the same way in the Sale, Return
 * and Purchase forms.
 *
 * The figure is fetched live from `/products/:id/current-stock` the moment a
 * product is picked — the same count the reports show, made from the invoices
 * on request. The product list only carries `computedStock`, a cache that is
 * rewritten when an invoice touches the product and can therefore lag behind
 * the reports (a changed counting rule, a recount, or a sale made from another
 * session all leave it stale). It is kept only as the figure shown while the
 * live one loads, or when that request fails. See utils/stockHelpers.ts.
 */
const ItemStockHint: React.FC<ItemStockHintProps> = ({ form, name, products, type }) => {
    const { t } = useTranslation();

    const productId = Form.useWatch(['items', name, 'product'], form) as string | undefined;
    const unit = Form.useWatch(['items', name, 'unit'], form) as string | undefined;
    const qtyValue = Form.useWatch(['items', name, 'qty'], form);

    const [live, setLive] = useState<LiveStock | null>(null);

    useEffect(() => {
        if (!productId) return;
        let cancelled = false;
        getProductCurrentStock(productId)
            .then(res => {
                // A reply for a product this row no longer shows must not land
                // on the one it shows now.
                if (!cancelled && res.data && typeof res.data === 'object') {
                    setLive({ productId, byUnit: res.data });
                }
            })
            .catch(() => {
                // Fall back to the cached figure on the product; nothing to show
                // the user here that the cached hint does not already show.
            });
        return () => {
            cancelled = true;
        };
    }, [productId]);

    if (!productId) return null;

    const product = products.find(p => p.id === productId);
    if (!product) return null;

    const qty = Number(qtyValue) || 0;
    const stock = live && live.productId === productId
        ? sumStockByUnit(live.byUnit, unit)
        : getProductStock(product, unit);
    const after = getStockAfterLine(stock, qty, type);

    return (
        <Tooltip title={t('stock_after_this_line') || 'Stock after this line'}>
            <div
                style={{
                    fontSize: 12,
                    marginTop: 2,
                    color: after < 0 ? '#cf1322' : '#389e0d',
                }}
            >
                {t('available_stock') || 'In stock'}: {stock.toLocaleString()}
                {qty > 0 && <> → {after.toLocaleString()}</>}
            </div>
        </Tooltip>
    );
};

export default ItemStockHint;
